import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, UtensilsCrossed } from "lucide-react";
import { api } from "@/lib/api";
import AuthGate from "@/components/AuthGate";

const TABS = ["Menù del giorno", "Preventivi", "Iscrizioni lista", "Candidature"];

function useNoIndex() {
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => document.head.removeChild(m);
  }, []);
}

function Inbox({ endpoint, renderItem, testid, emptyText }) {
  const [items, setItems] = useState(null);

  const load = () => api.get(endpoint).then((r) => setItems(r.data)).catch(() => toast.error("Errore di caricamento"));
  useEffect(() => { load(); }, [endpoint]);

  return (
    <div data-testid={testid}>
      <button onClick={load} className="mb-5 inline-flex items-center gap-2 text-sm text-terracotta font-semibold hover:text-terracotta-dark">
        <RefreshCw size={14} /> Aggiorna
      </button>
      {items === null ? (
        <p className="text-sm text-charred/50">Caricamento…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-charred/50">{emptyText}</p>
      ) : (
        <div className="space-y-4">{items.map(renderItem)}</div>
      )}
    </div>
  );
}

const fmt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export default function Admin() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState(0);

  useNoIndex();

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false));
  }, []);

  if (user === null) return <div className="min-h-screen bg-cream grid place-items-center text-charred/50">Caricamento…</div>;
  if (user === false) return <AuthGate subtitle="Area riservata staff" onLogin={setUser} />;

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="bg-charred text-cream">
        <div className="max-w-[1200px] mx-auto px-6 py-5 flex items-center justify-between">
          <p className="font-serif text-2xl">SAPORÉ <span className="text-sm font-sans text-cream/50">— Area staff</span></p>
          <div className="flex items-center gap-4">
            <span className="text-xs text-cream/60 hidden sm:block">{user.name}</span>
            <button onClick={logout} data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-cream/70 hover:text-cream">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-10">
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)} data-testid={`admin-tab-${i}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${tab === i ? "bg-terracotta text-cream" : "bg-sand text-charred/70 hover:bg-sand/70"}`}>
              {t}
            </button>
          ))}
        </div>

        {tab === 0 && (
          <div className="bg-sand border border-charred/10 rounded-lg p-10 text-center" data-testid="admin-menu-redirect">
            <div className="mx-auto w-14 h-14 rounded-full bg-terracotta/10 grid place-items-center text-terracotta">
              <UtensilsCrossed size={24} />
            </div>
            <p className="mt-4 font-serif text-2xl md:text-3xl text-charred">La gestione dei piatti ha una nuova casa</p>
            <p className="mt-2 text-sm text-charred/60 max-w-md mx-auto">
              Aggiungi, modifica, sposta ed elimina i piatti del giorno dalla nuova area dedicata, pensata anche per lo smartphone. Le modifiche compaiono subito sul sito pubblico.
            </p>
            <Link to="/gestione-menu" data-testid="admin-open-gestione-btn"
              className="mt-6 inline-block rounded-full bg-terracotta text-cream px-8 py-3 text-sm font-semibold hover:bg-terracotta-dark transition-colors">
              Apri Gestione Menù
            </Link>
          </div>
        )}

        {tab === 1 && (
          <Inbox endpoint="/admin/quotes" testid="admin-quotes-list" emptyText="Nessuna richiesta di preventivo."
            renderItem={(q) => (
              <div key={q.id} className="bg-sand border border-charred/10 rounded-lg p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-serif text-xl text-charred">{q.nome} <span className="text-sm font-sans text-terracotta">· {q.tipo_evento}</span></p>
                  <p className="text-xs text-charred/50">{fmt(q.created_at)}</p>
                </div>
                <p className="mt-2 text-sm text-charred/70">
                  {q.telefono} · {q.email}
                  {q.data_evento && <> · Data: {q.data_evento}</>}
                  {q.partecipanti && <> · {q.partecipanti} partecipanti</>}
                  {q.luogo && <> · {q.luogo}</>}
                  {q.trasporto && <> · Trasporto richiesto</>}
                </p>
                {q.menu_desiderato && <p className="mt-2 text-sm text-charred/70"><span className="font-semibold">Menù:</span> {q.menu_desiderato}</p>}
                {q.allergie && <p className="mt-1 text-sm text-terracotta-dark"><span className="font-semibold">Allergie:</span> {q.allergie}</p>}
                {q.note && <p className="mt-1 text-sm text-charred/60">{q.note}</p>}
              </div>
            )} />
        )}

        {tab === 2 && (
          <Inbox endpoint="/admin/subscribers" testid="admin-subscribers-list" emptyText="Nessuna iscrizione alla lista."
            renderItem={(s) => (
              <div key={s.id} className="bg-sand border border-charred/10 rounded-lg px-6 py-4 flex items-baseline justify-between gap-3">
                <p className="text-sm text-charred"><span className="font-semibold">{s.nome}</span> · {s.contatto}</p>
                <p className="text-xs text-charred/50 shrink-0">{fmt(s.created_at)}</p>
              </div>
            )} />
        )}

        {tab === 3 && (
          <Inbox endpoint="/admin/applications" testid="admin-applications-list" emptyText="Nessuna candidatura ricevuta."
            renderItem={(a) => (
              <div key={a.id} className="bg-sand border border-charred/10 rounded-lg p-6">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <p className="font-serif text-xl text-charred">{a.nome} {a.mansione && <span className="text-sm font-sans text-terracotta">· {a.mansione}</span>}</p>
                  <p className="text-xs text-charred/50">{fmt(a.created_at)}</p>
                </div>
                <p className="mt-2 text-sm text-charred/70">{a.telefono}{a.email && <> · {a.email}</>}{a.disponibilita && <> · Disponibilità: {a.disponibilita}</>}</p>
                {a.esperienza && <p className="mt-2 text-sm text-charred/70"><span className="font-semibold">Esperienza:</span> {a.esperienza}</p>}
                {a.note && <p className="mt-1 text-sm text-charred/60">{a.note}</p>}
              </div>
            )} />
        )}
      </div>
    </div>
  );
}
